import cx from 'classnames';
import { DetailedHTMLProps, HTMLAttributes, MouseEvent, ReactNode, forwardRef } from 'react';
import { IconButton } from '../presentational/buttons/IconButton';

type TAlertType = 'error' | 'warning' | 'info' | 'success';

export interface IAlertProps extends Omit<
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'children' | 'ref'
> {
  message: ReactNode;
  description?: ReactNode;
  type: TAlertType;
  className?: string;
  icon?: boolean | ReactNode;
  onClose?: () => void;
  centered?: boolean;
  clickable?: boolean;
  size?: 'large' | 'x-large';
}

export const Alert = forwardRef<HTMLDivElement, IAlertProps>(
  ({ message, description, type, className, icon = true, onClose, centered, clickable, size, ...rest }, ref) => {
    const normalizedIcon = icon === true ? <DefaultIconByType type={type} /> : icon;

    const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
      onClose?.();
      e.stopPropagation();
    };

    return (
      <div
        ref={ref}
        className={cx(`${CLASS_PREFIX} ${CLASS_PREFIX}--${type}`, className, {
          [`${CLASS_PREFIX}--with-description`]: description,
          [`${CLASS_PREFIX}--centered`]: centered,
          [`${CLASS_PREFIX}--clickable`]: clickable,
          [`${CLASS_PREFIX}--${size}`]: size
        })}
        {...rest}
      >
        {normalizedIcon && !centered && <div className={`${CLASS_PREFIX}__icon`}>{normalizedIcon}</div>}

        <div className={cx(`${CLASS_PREFIX}__content`, { [`${CLASS_PREFIX}__content--centered`]: centered })}>
          <div className={cx(`${CLASS_PREFIX}__message`)}>
            {centered && normalizedIcon && <div className={`${CLASS_PREFIX}__icon`}>{normalizedIcon}</div>}
            {message}
          </div>
          {description && <div className={`${CLASS_PREFIX}__description`}>{description}</div>}
        </div>

        {onClose && (
          <IconButton icon={<i className="fa fa-times" />} onClick={handleClose} size="small" variant="text" />
        )}
      </div>
    );
  }
);

const DefaultIconByType = ({ type }: { type: TAlertType }) => {
  switch (type) {
    case 'error':
      return <i className="fa fa-exclamation-triangle" />;
    case 'warning':
      return <i className="fa fa-exclamation-triangle" />;
    case 'info':
      return <i className="fa fa-info-circle" />;
    case 'success':
      return <i className="fa fa-check-circle" />;
  }
};

const CLASS_PREFIX = 'rio-alert-v2';
